import { Maximize2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog.tsx';

/** Tall enough for three collapsed notes (three lines and "Read more" each); then it scrolls. */
export const BLOCK_LIST_CLASS = 'max-h-[29rem] overflow-y-auto pr-1';

/**
 * A notes or tasks card (project Overview and Jobs tab, an expanded job), with "Full screen": a large dialog holding only that
 * block, its list uncapped. `children(full)` draws the block for either place. `filter` and
 * `action` sit before the "Full screen" icon (the project's notes: origin filter and Add note);
 * both are in the full-screen header too, and their state is the caller's, so it carries over.
 */
export function BlockCard({
  title,
  filter,
  action,
  children,
}: {
  title: string;
  filter?: ReactNode;
  action?: ReactNode;
  children: (full: boolean) => ReactNode;
}) {
  const [full, setFull] = useState(false);
  return (
    <Card className="p-5" aria-label={title}>
      {/* Top-aligned: the filter's markers under its toggles never shift the title or buttons. */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <h3 className="text-muted-foreground text-xs leading-7 font-semibold">{title}</h3>
        <div className="ml-auto flex flex-wrap items-start justify-end gap-2">
          {filter}
          {action}
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setFull(true)}
            aria-label={`Full screen ${title.toLowerCase()}`}
            title="Full screen"
            data-testid="block-full-screen"
          >
            <Maximize2 />
          </Button>
        </div>
      </div>
      {children(false)}
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent
          className="h-[calc(100dvh-4rem)] max-w-5xl content-start"
          aria-describedby={undefined}
          // Radix would focus the first control (the toggle's All), which then reads as chosen;
          // the dialog itself takes focus instead, so the keyboard starts inside it.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).focus();
          }}
          data-testid="block-full-screen-dialog"
        >
          <DialogHeader className="flex-row flex-wrap items-start gap-2">
            <DialogTitle>{title}</DialogTitle>
            {(filter || action) && (
              <div className="ml-auto flex flex-wrap items-start justify-end gap-2">
                {filter}
                {action}
              </div>
            )}
          </DialogHeader>
          {full && children(true)}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
